import * as React from 'react';

import {
  findWebflowUtilitiesSlot,
  measureUtilitiesSlotAnchor,
  prepareWebflowUtilitiesSlot,
  type UtilitiesSlotAnchor,
  type WebflowUtilitiesMount,
} from '@/lib/webflow-poc/webflow-utilities-slot';

type UseWebflowUtilitiesSlotAnchorOptions = {
  containerRef: React.RefObject<HTMLElement | null>;
  /** Re-bind when Webflow HTML is replaced. */
  htmlKey: string | null | undefined;
  enabled: boolean;
  utilitiesMount: WebflowUtilitiesMount;
};

const anchorsEqual = (
  a: UtilitiesSlotAnchor | null,
  b: UtilitiesSlotAnchor | null
): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.top === b.top &&
    a.left === b.left &&
    a.width === b.width &&
    a.height === b.height
  );
};

/**
 * Tracks the Webflow utilities mount element and returns a viewport-fixed
 * anchor for the TW icon cluster portal.
 *
 * Webflow mobile/iPad menus open via class toggles + CSS transforms, which
 * often do NOT fire ResizeObserver until the user interacts again. Without
 * MutationObserver / transition / click re-syncs, the TW menu stays missing
 * until a touch — the bug QRCA reported on staging.
 */
export const useWebflowUtilitiesSlotAnchor = (
  options: UseWebflowUtilitiesSlotAnchorOptions
): UtilitiesSlotAnchor | null => {
  const { containerRef, htmlKey, enabled, utilitiesMount } = options;
  const [anchor, setAnchor] = React.useState<UtilitiesSlotAnchor | null>(null);
  const syncTimeoutsRef = React.useRef<number[]>([]);
  const preparedSlotRef = React.useRef<HTMLElement | null>(null);
  // Ignore MutationObserver noise we cause ourselves in prepare/sync.
  const ignoreMutationsRef = React.useRef(false);
  const burstScheduledRef = React.useRef(false);

  React.useEffect(() => {
    if (!enabled) {
      setAnchor(null);
      preparedSlotRef.current = null;
      return;
    }

    const clearScheduled = () => {
      for (const id of syncTimeoutsRef.current) {
        window.clearTimeout(id);
      }
      syncTimeoutsRef.current = [];
    };

    const applyAnchor = (next: UtilitiesSlotAnchor | null) => {
      setAnchor((prev) => (anchorsEqual(prev, next) ? prev : next));
    };

    const sync = () => {
      const container = containerRef.current;
      if (!container) {
        applyAnchor(null);
        return;
      }

      const slot = findWebflowUtilitiesSlot(container, utilitiesMount);
      if (!slot) {
        preparedSlotRef.current = null;
        applyAnchor(null);
        return;
      }

      // prepareWebflowUtilitiesSlot mutates style/aria — suppress observer feedback.
      ignoreMutationsRef.current = true;
      try {
        if (preparedSlotRef.current !== slot) {
          prepareWebflowUtilitiesSlot(slot);
          preparedSlotRef.current = slot;
        }
        applyAnchor(measureUtilitiesSlotAnchor(slot));
      } finally {
        // Defer clearing so attribute mutations from prepare are flushed first.
        window.queueMicrotask(() => {
          ignoreMutationsRef.current = false;
        });
      }
    };

    /** Webflow menu open/close is animated — remeasure across the transition. */
    const scheduleSyncBurst = () => {
      // One burst at a time — continuous Webflow transitions must not stack.
      if (burstScheduledRef.current) return;
      burstScheduledRef.current = true;
      clearScheduled();
      sync();
      window.requestAnimationFrame(() => {
        sync();
        window.requestAnimationFrame(sync);
      });
      for (const delayMs of [50, 150, 300, 500, 800]) {
        syncTimeoutsRef.current.push(
          window.setTimeout(() => {
            sync();
            if (delayMs === 800) {
              burstScheduledRef.current = false;
            }
          }, delayMs) as unknown as number
        );
      }
    };

    const raf = window.requestAnimationFrame(sync);

    window.addEventListener('resize', sync);
    window.addEventListener('scroll', sync, true);
    window.addEventListener('orientationchange', scheduleSyncBurst);
    window.addEventListener('pageshow', sync);
    document.addEventListener('visibilitychange', sync);

    const container = containerRef.current;

    const mutationObserver =
      typeof MutationObserver !== 'undefined' && container
        ? new MutationObserver(() => {
            if (ignoreMutationsRef.current) return;
            scheduleSyncBurst();
          })
        : null;
    if (mutationObserver && container) {
      // Only class / expanded state — NOT style/aria-hidden (we write those).
      mutationObserver.observe(container, {
        attributes: true,
        attributeFilter: ['class', 'aria-expanded'],
        childList: false,
        subtree: true,
      });
    }

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' && container
        ? new ResizeObserver(() => {
            if (ignoreMutationsRef.current) return;
            sync();
          })
        : null;
    if (resizeObserver && container) {
      resizeObserver.observe(container);
      const slot = findWebflowUtilitiesSlot(container, utilitiesMount);
      if (slot) {
        resizeObserver.observe(slot);
      }
    }

    const onMenuInteraction = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Element) || !container) return;
      // Only hamburger / nav toggles — not every click inside the page HTML.
      if (
        target.closest(
          '.w-nav-button, .w-nav-menu, .w-nav, [data-nav-menu-open], [aria-controls]'
        )
      ) {
        scheduleSyncBurst();
      }
    };

    document.addEventListener('click', onMenuInteraction, true);
    document.addEventListener('touchend', onMenuInteraction, true);
    container?.addEventListener('transitionend', scheduleSyncBurst);
    container?.addEventListener('animationend', scheduleSyncBurst);

    return () => {
      clearScheduled();
      burstScheduledRef.current = false;
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', sync);
      window.removeEventListener('scroll', sync, true);
      window.removeEventListener('orientationchange', scheduleSyncBurst);
      window.removeEventListener('pageshow', sync);
      document.removeEventListener('visibilitychange', sync);
      document.removeEventListener('click', onMenuInteraction, true);
      document.removeEventListener('touchend', onMenuInteraction, true);
      container?.removeEventListener('transitionend', scheduleSyncBurst);
      container?.removeEventListener('animationend', scheduleSyncBurst);
      mutationObserver?.disconnect();
      resizeObserver?.disconnect();
      preparedSlotRef.current = null;
    };
  }, [
    containerRef,
    htmlKey,
    enabled,
    utilitiesMount.kind,
    utilitiesMount.name,
  ]);

  return anchor;
};
